import {Injectable} from '@angular/core';
import {DataService} from './data.service';
import {firstValueFrom} from 'rxjs';
import {PrintBuilderService} from './print-builder.service';

@Injectable({providedIn: 'root'})
export class PaymentDocumentService {
  constructor(private data: DataService, private printer: PrintBuilderService) {
  }

  /** Load the unified entry payload for a payment header id */
  async getEntry(id: string): Promise<any> {
    // Payment header info (t_header) is returned by the 'entry' endpoint
    // and may be wrapped in a Data property depending on the API.
    const raw = await firstValueFrom(this.data.getData('entry', id));
    return (raw && (raw as any).Data) ? (raw as any).Data : raw;
  }

  /** Safely extract t_header object from an entry payload */
  extractTHeader(entry: any): any {
    const e = (entry && (entry as any).Data) ? (entry as any).Data : (entry || {});
    // t_header may be at root or under item
    return (e && (e.t_header || e?.item?.t_header || e?.item?.header || e.header)) || {};
  }

  /** Return whether a given payment id is already marked printed according to t_header from entry */
  async isPrinted(id: string): Promise<boolean> {
    try {
      const entry = await this.getEntry(id);
      const th = this.extractTHeader(entry);
      return Boolean(th?.printed);
    } catch {
      return false;
    }
  }

  /** Build a map of id -> printed (from t_header) for a set of selected payment ids */
  async getPrintedMap(ids: (string | number)[]): Promise<Record<string, boolean>> {
    const map: Record<string, boolean> = {};
    if (!Array.isArray(ids) || !ids.length) {
      return map;
    }
    const promises = ids.map(async (raw) => {
      const id = String(raw);
      try {
        const entry = await this.getEntry(id);
        const th = this.extractTHeader(entry);
        map[id] = Boolean(th?.printed);
      } catch {
        map[id] = false;
      }
    });
    await Promise.all(promises);
    return map;
  }

  /** Build print HTML for payments using the shared PrintBuilderService */
  buildPrintHtml(vm: any, docType: string = 'ar'): string {
    // Route to the shared builder. For receipts, pass 'receipt' so the proper layout is used.
    return this.printer.buildPrintHtml(vm, docType || 'ar');
  }

  /**
   * Build an AP Check "Paid Copy" HTML using the existing payment-print layout as a basis.
   * Expects a VM that contains:
   *  - vm.check: array of checks with fields like companyName, bankName, number, date, amount, amountInWords,
   *              payeeName, payeeAddress1/2/3, transitNumber, institutionNumber, accountNumber
   *  - vm.printLayout.checkStock: positioning info (top/left/right) and print flags used by the original template
   */
  buildApCheckHtml(vm: any): string {
    const checks: any[] = Array.isArray(vm?.check) ? vm.check : [];
    const cs = vm?.printLayout?.checkStock || {};
    const safe = (v: any) => (v === null || v === undefined ? '' : String(v));
    const toMoney = (n: any) => Number(n || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
    const toDate = (d: any) => {
      try {
        if (!d) {
          return '';
        }
        const s = String(d);
        // Extract date parts directly from ISO string to avoid timezone shifts
        const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) {
          return `${m[2]}/${m[3]}/${m[1]}`;
        }
        const dd = new Date(d);
        if (isNaN(dd.getTime())) {
          return s;
        }
        const mm = String(dd.getMonth() + 1).padStart(2, '0');
        const day = String(dd.getDate()).padStart(2, '0');
        const yyyy = dd.getFullYear();
        return `${mm}/${day}/${yyyy}`;
      } catch {
        return String(d ?? '');
      }
    };

    const styles = `
      <style>
        body { font-family: Arial, Helvetica, sans-serif; color: #000; }
        .check-page { page-break-after: always; position: relative; }
        .check-header { position: relative; width: 100%; height: 4in; }
        .check-border { position: relative; width: 100%; height: 100%; }
        .company-wrapper, .bank-wrapper, .check-number, .check-date,
        .check-amount, .pay, .amount-text, .to-order, .payee { position: absolute; }
        .text-right { text-align: right; }
        .float-end { float: right; }
        .signature-line { position: absolute; bottom: 20px; right: 60px; padding-right: 20px; text-decoration: overline; font-size: 90%; letter-spacing: 3px; width: auto; margin: 0; }
        .micr { font-size: 0; margin-top: 6px; }
        .stub { margin-top: 12px; border-top: 2px solid #000; padding-top: 8px; }
        .watermark-paid { position: absolute; inset: 0; display:flex; align-items:center; justify-content:center; }
        .watermark-paid span { opacity: 0.08; font-size: 120px; font-weight: 900; transform: rotate(-25deg); letter-spacing: 10px; }
        img { max-width: 100%; }
      /* Stub tables (modeled after payment-print.component.html) */
      .check-stub-table { width: 100%; border-collapse: collapse; margin-top: 6px; }
      .check-stub-table th, .check-stub-table td { padding: 4px 6px; border-bottom: 1px solid #ddd; font-size: 12px; }
      .check-stub-table th { border-bottom: 2px solid #000; text-align: left; }
      .w1 { width: 20%; }
      .nowrap { white-space: nowrap; }
      .text-right { text-align: right; }
      .text-center { text-align: center; }
      .text-bold { font-weight: bold; }
      </style>`;

    const renderMICR = (chk: any) => {
      try {
        // Render MICR line using provided svg assets paths (as in original template). If not available, skip.
        const number = safe(chk.number);
        const transit = safe(chk.transitNumber);
        const inst = safe(chk.institutionNumber);
        const account = safe(chk.accountNumber);
        // Minimal safe fallback: show plain text MICR if image assets not present
        return `
          <div class="micr">
            <div style="font-family: monospace; font-size:14px; text-align:center;">
              ${number}  |: ${transit} :|  ${inst}  ${account}
            </div>
          </div>`;
      } catch {
        return '';
      }
    };

    const pages = checks.map((check) => {
      // Per-check stub context
      const appliedList: any[] = Array.isArray(check?.applied) ? check.applied : (Array.isArray(vm?.applied) ? vm.applied : []);
      const paymentAmountRaw = (check?.paymentAmount ?? vm?.paymentAmount ?? check?.amount ?? 0);
      const totalPaidRaw = (check?.totalPaid ?? vm?.totalPaid ?? check?.amount ?? 0);
      const paymentAmount = Math.abs(Number(paymentAmountRaw || 0));
      const totalPaid = Math.abs(Number(totalPaidRaw || 0));
      const diff = paymentAmount - totalPaid;
      const hasDiff = Math.round(diff * 100) !== 0;
      const diffLabel = diff > 0 ? 'Unapplied' : 'Over Applied';
      return `
        <div class="check-page">
          <div class="check-header">
            <div class="check-border">
              ${cs.print_company === true ? `
              <div class="company-wrapper" style="top:${cs.company_top || 0}px; left:${cs.company_left || 0}px;">
                ${safe(check.companyName)}<br/>
                ${check.companyDBA ? `${safe(check.companyDBA)}<br/>` : ''}
                ${safe(check.companyAddress1)}<br/>
                ${safe(check.companyAddress2)}<br/>
                ${safe(check.companyPhone)}
              </div>` : ''}

              ${cs.print_bank === true ? `
              <div class="bank-wrapper" style="top:${cs.bank_top || 0}px; left:${cs.bank_left || 0}px;">
                ${safe(check.bankName)}<br/>
                ${safe(check.bankAddress1)}<br/>
                ${safe(check.bankAddress2)}
              </div>` : ''}

              ${cs.print_check_number === true ? `
              <div class="check-number" style="top:${cs.check_number_top || 0}px; right:${cs.check_number_right || 0}px;">
                ${safe(check.number)}
              </div>` : ''}

              <div class="check-date" style="top:${cs.date_top || 0}px; right:${cs.date_right || 0}px;">
                <div>${toDate(check.date)}</div>
              </div>

              <div class="check-amount" style="top:${cs.payment_amount_top || 0}px; right:${cs.payment_amount_right || 0}px;">
                $${Number(check.amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </div>

              ${cs.print_pay_to === true ? `
              <div class="pay" style="top:${(cs.text_amount_top || 0)}px;">PAY</div>` : ''}

              <div class="amount-text text-right float-end" style="top:${(cs.text_amount_top || 0)}px; left:${(cs.text_amount_left || 0)}px; right:auto;">
                ${safe(check.amountInWords)} * * *
              </div>

              ${cs.to_order_of === true ? `
              <div class="to-order" style="top:${(cs.text_amount_top || 0) - 10}px; left:${(cs.vendor_address_left || 0) - 80}px;">
                TO THE <br/> ORDER <br/> OF
              </div>` : ''}

              <div class="payee" style="top:${(cs.vendor_address_top || 0)}px; left:${(cs.vendor_address_left || 0)}px;">
                ${safe(check.payeeName)}<br/>
                ${safe(check.payeeAddress1)}<br/>
                ${safe(check.payeeAddress2)}<br/>
                ${safe(check.payeeAddress3)}
              </div>

              ${cs.print_signature_line === true ? `
              <pre class="signature-line">       authorized signature       </pre>` : ''}

              ${check.printed ? `<div class="watermark-paid"><span>PAID</span></div>` : ''}
            </div>
            ${renderMICR(check)}
          </div>
          <div class="stub">
            ${cs.print_company === true ? `<span>From ${safe(check.companyName)}</span><br/>` : ''}
            ${Number(check?.amount) < 0 ? `<span class="text-danger"> Refund</span>` : ''}
            Cheque ${cs.print_check_number === true ? `#${safe(check.number)}` : ''} to ${safe(check.payeeName)} dated ${toDate(check.date)}
            <br/>
            <table class="check-stub-table">
              <tr>
                <th colspan="2">Payment Amount :</th>
                <th class="text-right">${toMoney(paymentAmount)}</th>
              </tr>
              <tr>
                <th class="w1">Number</th>
                <th class="text-center w1">Date</th>
                <th class="text-right w1">Applied</th>
              </tr>
              ${appliedList.slice(0, 15).map(p => `
                <tr>
                  <td class="nowrap">${safe(p.number || p.applied_header_id || p.id)}</td>
                  <td class="text-center nowrap">${toDate(p.header_date || p.applied_date || p.date)}</td>
                  <td class="text-right">${toMoney(p.applied_amount)}</td>
                </tr>`).join('')}
              ${appliedList.length <= 15 ? `
                <tr>
                  <td colspan="2" class="text-bold">
                    ${hasDiff ? diffLabel + ':' : ''}
                  </td>
                  <td class="text-bold text-right">
                    ${hasDiff ? toMoney(diff) : ''}
                  </td>
                </tr>` : ''}
            </table>
            ${appliedList.length > 15 ? `
              <table class="check-stub-table">
                <tr><th colspan="3">&nbsp;</th></tr>
                <tr>
                  <th class="w1">Number</th>
                  <th class="text-center w1">Date</th>
                  <th class="text-right w1">Applied</th>
                </tr>
                ${appliedList.slice(15, 30).map(p => `
                  <tr>
                    <td class="nowrap">${safe(p.number || p.applied_header_id || p.id)}</td>
                    <td class="text-center nowrap">${toDate(p.header_date || p.applied_date || p.date)}</td>
                    <td class="text-right">${toMoney(p.applied_amount)}</td>
                  </tr>`).join('')}
                <tr>
                  <td colspan="2" class="text-bold">${hasDiff ? diffLabel + ':' : ''}</td>
                  <td class="text-bold text-right">${hasDiff ? toMoney(diff) : ''}</td>
                </tr>
              </table>` : ''}

            ${appliedList.length > 30 ? `
              <table class="check-stub-table">
                <tr><th colspan="3">&nbsp;</th></tr>
                <tr>
                  <th class="w1">Number</th>
                  <th class="text-center w1">Date</th>
                  <th class="text-right w1">Applied</th>
                </tr>
                ${appliedList.slice(30, 45).map(p => `
                  <tr>
                    <td class="nowrap">${safe(p.number || p.applied_header_id || p.id)}</td>
                    <td class="text-center nowrap">${toDate(p.header_date || p.applied_date || p.date)}</td>
                    <td class="text-right">${toMoney(p.applied_amount)}</td>
                  </tr>`).join('')}
                <tr>
                  <td colspan="2" class="text-bold">${hasDiff ? diffLabel + ':' : ''}</td>
                  <td class="text-bold text-right">${hasDiff ? toMoney(diff) : ''}</td>
                </tr>
              </table>` : ''}

            ${appliedList.length > 45 ? `
              <table class="check-stub-table">
                <tr><th colspan="3">&nbsp;</th></tr>
                <tr>
                  <th class="w1">Number</th>
                  <th class="text-center w1">Date</th>
                  <th class="text-right w1">Applied</th>
                </tr>
                ${appliedList.slice(45, 145).map(p => `
                  <tr>
                    <td class="nowrap">${safe(p.number || p.applied_header_id || p.id)}</td>
                    <td class="text-center nowrap">${toDate(p.header_date || p.applied_date || p.date)}</td>
                    <td class="text-right">${toMoney(p.applied_amount)}</td>
                  </tr>`).join('')}
                <tr>
                  <td colspan="2" class="text-bold">${hasDiff ? diffLabel + ':' : ''}</td>
                  <td class="text-bold text-right">${hasDiff ? toMoney(diff) : ''}</td>
                </tr>
              </table>` : ''}
          </div>
        </div>`;
    }).join('');

    return `<!DOCTYPE html>
    <html lang=""><head><meta charset="utf-8"/>${styles}<title>Check - Paid Copy</title></head>
    <body>${pages}</body></html>`;
  }

  /** Public wrapper to print arbitrary HTML using a robust iframe flow (waits for assets). */
  async printHtml(html: string): Promise<void> {
    return this.printHtmlInIframe(html);
  }

  /** Private: print HTML in a hidden iframe and wait until it is fully loaded before printing */
  private printHtmlInIframe(html: string): Promise<void> {
    return new Promise<void>((resolve) => {
      try {
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow?.document || iframe.contentDocument as Document;
        doc.open();
        doc.write(html);
        doc.close();

        // Guard to ensure we only invoke print once, even if multiple events fire
        let finished = false;
        let safetyTimer: any = null;
        const done = () => {
          if (finished) {
            return;
          }
          finished = true;
          try {
            if (safetyTimer) {
              try {
                clearTimeout(safetyTimer);
              } catch {
              }
            }
            iframe.contentWindow?.focus();
            // Use a short delay to ensure styles/images are applied
            setTimeout(() => {
              try {
                iframe.contentWindow?.print();
              } catch {
              }
              setTimeout(() => {
                try {
                  document.body.removeChild(iframe);
                } catch {
                }
                resolve();
              }, 100);
            }, 50);
          } catch {
            try {
              document.body.removeChild(iframe);
            } catch {
            }
            resolve();
          }
        };

        // Wait for images to load as well
        const tryWaitForImages = () => {
          const imgs = Array.from(doc.images || []);
          if (!imgs.length) {
            done();
            return;
          }
          let loaded = 0;
          const check = () => {
            if (++loaded >= imgs.length) {
              done();
            }
          };
          imgs.forEach((img) => {
            if ((img as any).complete) {
              check();
            } else {
              img.addEventListener('load', check, {once: true});
              img.addEventListener('error', check, {once: true});
            }
          });
          // Safety timeout
          safetyTimer = setTimeout(done, 5000);
        };

        if (iframe.contentWindow?.document?.readyState === 'complete') {
          tryWaitForImages();
        } else {
          iframe.onload = () => tryWaitForImages();
        }
      } catch {
        resolve();
      }
    });
  }
}
